// src/components/conduction-social/ActivityFeedPost.tsx
import appSettings from "@/appsettings.json";
import ProjectVisual from "@/components/conduction-social/ProjectVisual";
import { AVATAR_SIZE, SessionContext } from "@/context/SessionContext";
import {
	Project,
	ProjectPublication,
	ProjectSubmission
} from "@/javascript/project-objects";
import {
	Avatar,
	Badge,
	Box,
	Button,
	Card,
	Divider,
	Group,
	Image,
	Paper,
	Skeleton,
	Stack,
	Table,
	Text,
	Title,
	useMantineTheme,
	rgba
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconMusic, IconPlayerPause, IconPlayerPlay, IconUser } from "@tabler/icons-react";
import React, {
	memo,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import ReactPlayer from "react-player";

import {
	announcePlayback,
	EVT_PLAYBACK,
	onHub,
} from "@/javascript/playback-hub";
import { User } from "@/javascript/user-objects";
import { Spoiler } from "@mantine/core";
import { Utils } from "@/javascript/utils";

/* -------------------------- shared helpers -------------------------- */

function formatRelativeWithLabel(
	date?: Date | null,
	label: "Submitted" | "Published" | "Posted" = "Submitted"
): string {
	if (!date) return "";
	const now = Date.now();
	const diffSec = Math.max(0, Math.floor((now - date.getTime()) / 1000));
	if (diffSec < 60) return `${label} just now`;
	const min = Math.floor(diffSec / 60);
	if (min < 60) return `${label} ${min} minute${min === 1 ? "" : "s"} ago`;
	const hrs = Math.floor(min / 60);
	if (hrs < 24) return `${label} ${hrs} hour${hrs === 1 ? "" : "s"} ago`;
	const days = Math.floor(hrs / 24);
	if (days < 7) return `${label} ${days} day${days === 1 ? "" : "s"} ago`;
	const weeks = Math.floor(days / 7);
	if (weeks < 5) return `${label} ${weeks} week${weeks === 1 ? "" : "s"} ago`;
	const months = Math.floor(days / 30);
	if (months < 12) return `${label} ${months} month${months === 1 ? "" : "s"} ago`;
	const years = Math.floor(days / 365);
	return `${label} ${years} year${years === 1 ? "" : "s"} ago`;
}

/* -------------------------- shared header --------------------------- */

type PostHeaderProps = {
	owner: User | null | undefined;
	headline: string;
	rightTimeLabel: string;
	goToUserFeed: (uid: string) => void;
};

const PostHeader: React.FC<PostHeaderProps> = memo(({ owner, headline, rightTimeLabel, goToUserFeed }) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const [avatar, setAvatar] = useState<JSX.Element | null>(null);
	const session = useContext(SessionContext);
	const baseColorName = Utils.getColorFromString(owner?.id ?? "unknown");

	if (!session) throw new Error("PostHeader must be a child of SessionContext.");

	useEffect(() => {
		let cancelled = false;
		if (owner) {
			session.fetchProfileAvatar(owner).then((av) => { if (!cancelled) setAvatar(av); });
		} else {
			setAvatar(null);
		}
		return () => { cancelled = true; };
	}, [owner, session]);

	const ownerName = owner?.username ?? "Unknown user";

	const pastelBg = rgba(theme.colors[baseColorName][5], 0.05); // mid shade, light alpha
	const pastelBorder = rgba(theme.colors[baseColorName][5], 0.15); // mid shade, medium alpha

	return (
		<Box
			// don't pass 'bg' with just the color name; set a custom pastel BG via style
			style={{
				backgroundColor: pastelBg,
				border: `1px solid ${pastelBorder}`,
			}}
			w="100%"
			p="sm"
		>
			<Group justify="space-between" align="center">
				<Group gap="xs">
					<Button
						leftSection={avatar}
						variant="subtle"
						size="md"
						p={2}
						onClick={() => owner != null && goToUserFeed(owner.id)}
					>
						<Text fw={700} fz={isMobile ? "sm" : "md"} c={theme.colors.blue[8]}>
							{ownerName}
						</Text>
					</Button>
					{!isMobile && (
						<Text fw={700} fz={isMobile ? "sm" : "md"} c={theme.colors.blue[8]}>
							{headline}
						</Text>
					)}
				</Group>
				<Text fz="xs" c="dimmed">
					{rightTimeLabel}
				</Text>
			</Group>
		</Box>
	);
});
PostHeader.displayName = "PostHeader";

/* -------------------------- submission post ------------------------- */

type SubmissionPostProps = { submission: ProjectSubmission };

const SubmissionPost: React.FC<SubmissionPostProps> = ({ submission }) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

	const CARD_PADDING = isMobile ? 8 : 20;
	const submissionThumb = `${appSettings.S3BucketBaseUrl}SubmissionThumbnails/${submission.id}.jpg`;
	const videoUrl = `${appSettings.S3BucketBaseUrl}SubmissionVideos/${submission.id}.mp4`;
	const feedbackVideoUrl = `${appSettings.S3BucketBaseUrl}SubmissionFeedbackVideos/${submission.id}.mp4`;

	const [hoverSubmissionVideo, setHoverSubmissionVideo] = useState(false);
	const [hoverFeedbackVideo, setHoverFeedbackVideo] = useState(false);
	const [submissionVideoLoaded, setSubmissionVideoLoaded] = useState(false);
	const [feedbackVideoLoaded, setFeedbackVideoLoaded] = useState(false);
	const [isSubmissionVideoPlaying, setIsSubmissionVideoPlaying] = useState(false);
	const [isFeedbackVideoPlaying, setIsFeedbackVideoPlaying] = useState(false);
	const [profileAvatars, setProfileAvatars] = useState<Map<string, JSX.Element>>(new Map());
	const [meetTheBandExpanded, setMeetTheBandExpanded] = useState(false);
	const [showFeedbackVideo, setShowFeedbackVideo] = useState(false);
	const [overlayFaded, setOverlayFaded] = useState(false);
	const inactivityTimerRef = useRef<number | null>(null);

	// How long to wait before fading (ms)
	const INACTIVITY_DELAY = 2000;

	const kickInactivityTimer = useCallback(() => {
		setOverlayFaded(false);
		if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
		inactivityTimerRef.current = window.setTimeout(() => {
			setOverlayFaded(true);
		}, INACTIVITY_DELAY) as unknown as number;
	}, []);

	useEffect(() => {
		// Clear timer on unmount or when hover stops
		return () => {
			if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
		};
	}, []);

	const playerRef = useRef<HTMLDivElement | null>(null);
	const feedbackVideoPlayerRef = useRef<HTMLDivElement | null>(null);

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error("SubmissionPost must be a child of SessionContext.");
	}

	const fetchProfileAvatars = useCallback(async () => {
		if (profileAvatars.size > 0) return; // already fetched

		const profileAvatarsMap = new Map<string, JSX.Element>();
		await Promise.all(submission.instrumentalistTasks.map(async task => {
			if (task.instrumentalist === null) return;
			const avatar = await session.fetchProfileAvatar(task.instrumentalist);
			profileAvatarsMap.set(task.instrumentalist.id, avatar);
		}));
		setProfileAvatars(profileAvatarsMap);
	}, [submission, session]);

	useEffect(() => {
		const unsubPub = onHub<CustomEvent>(EVT_PLAYBACK, () => {
			setIsSubmissionVideoPlaying(false);
			setIsFeedbackVideoPlaying(false);
		});
		const unsubSub = onHub<CustomEvent>(EVT_PLAYBACK, (e) => {
			const otherId = (e as CustomEvent).detail?.key as string | undefined;
			if (otherId && otherId !== submission.id + "-submission") {
				setIsSubmissionVideoPlaying(false);
			}
			if (otherId !== submission.id + "-feedback") {
				setIsFeedbackVideoPlaying(false);
			}
		});
		return () => { unsubPub(); unsubSub(); };
	}, [submission.id]);

	useEffect(() => {
		void fetchProfileAvatars();
	}, [fetchProfileAvatars]);

	const handleVideoThumbClick = () => {
		setSubmissionVideoLoaded(true);
		announcePlayback(submission.id + "-submission");
		setIsSubmissionVideoPlaying(true);
	};

	const handleFeedbackVideoThumbClick = () => {
		setFeedbackVideoLoaded(true);
		announcePlayback(submission.id + "-feedback");
		setIsFeedbackVideoPlaying(true);
	};

	const toggleSubmissionVideoPlay = () => {
		if (!submissionVideoLoaded) {
			handleVideoThumbClick();
			return;
		}
		kickInactivityTimer();
		if (!isSubmissionVideoPlaying) {
			announcePlayback(submission.id + "-submission");
			setIsSubmissionVideoPlaying(true);
		} else {
			setIsSubmissionVideoPlaying(false);
		}
	};

	const toggleFeedbackVideoPlay = () => {
		if (!feedbackVideoLoaded) {
			handleFeedbackVideoThumbClick();
			return;
		}
		kickInactivityTimer();
		if (!isFeedbackVideoPlaying) {
			announcePlayback(submission.id + "-feedback");
			setIsFeedbackVideoPlaying(true);
		} else {
			setIsFeedbackVideoPlaying(false);
		}
	};

	const feedbackProviderName = submission.feedbackProvider?.firstName != null && submission.feedbackProvider?.lastName != null ?
		`${submission.feedbackProvider.firstName} ${submission.feedbackProvider.lastName}` :
		submission.feedbackProvider?.username ?? "A musician";

	return (
		<Card.Section p={CARD_PADDING}>
			<Stack>
				<Title order={3} c={theme.colors.blue[7]}>
					{submission.submissionProjectName ?? `A project by ${submission.projectOwner?.username ?? "a Conduction user"}`}
				</Title>

				<Box
					style={{ position: "relative", borderRadius: 12, overflow: "hidden" }}
					onMouseEnter={() => { setHoverSubmissionVideo(true); kickInactivityTimer(); }}
					onMouseLeave={() => {
						setHoverSubmissionVideo(false);
						if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
					}}
					onMouseMove={() => { if (hoverSubmissionVideo) kickInactivityTimer(); }}
				>
					{!submissionVideoLoaded ? (
						<Box style={{ position: "relative" }} onClick={handleVideoThumbClick}>
							<Image src={submissionThumb} alt={submission.submissionProjectName ?? ""} w="100%" fit="cover" radius="md" />
							{hoverSubmissionVideo && (
								<CenterOverlay isMobile={isMobile} playing={false} onClick={toggleSubmissionVideoPlay} />
							)}
						</Box>
					) : (
						<Box style={{ position: "relative", width: "100%", aspectRatio: "16/9", background: "black" }} ref={playerRef}>
							<ReactPlayer
								src={videoUrl}
								width="100%"
								height="100%"
								playing={isSubmissionVideoPlaying}
								controls
								onPlay={() => {
									announcePlayback(submission.id + "-submission");
									setIsSubmissionVideoPlaying(true);
								}}
								onPause={() => setIsSubmissionVideoPlaying(false)}
								onEnded={() => setIsSubmissionVideoPlaying(false)}
							/>
							{hoverSubmissionVideo && (
								<CenterOverlay
									isMobile={isMobile}
									playing={isSubmissionVideoPlaying}
									onClick={toggleSubmissionVideoPlay}
									faded={overlayFaded}
								/>
							)}
						</Box>
					)}
				</Box>

				{submission.instrumentalistTasks.length > 0 && (
					<Stack w="100%">
						<Divider mt={20} />
						<Group onClick={() => setMeetTheBandExpanded((e) => !e)}>
							<Title order={3} c={theme.colors.blue[7]}>
								Meet the Band
							</Title>
							<Avatar.Group>
								{Array.from(profileAvatars.values()).slice(0, 4) /* Show up to 4 avatars */}
								{submission.instrumentalistTasks.length > 5 ? ( // show +N avatar if more than 5
									<Avatar size={AVATAR_SIZE} radius="xl" color="gray">
										+{submission.instrumentalistTasks.length - 4}
									</Avatar>
								) : (
									Array.from(profileAvatars.values())[4] // Just show the 5th avatar if there is only one more
								)}
							</Avatar.Group>
						</Group>

						<Spoiler
							w="100%"
							showLabel="Show more"
							hideLabel="Hide"
							expanded={meetTheBandExpanded}
							onExpandedChange={setMeetTheBandExpanded}
							maxHeight={170}
						>
							<Paper withBorder radius="md" p="xs" shadow="sm">
								<Table w="100%" striped highlightOnHover stickyHeader onClick={() => setMeetTheBandExpanded((e) => !e)}>
									<Table.Thead>
										<Table.Tr>
											<Table.Th miw={180}><Group><Title order={4}>Instrument</Title><IconMusic size={16} /></Group></Table.Th>
											<Table.Th><Group><Title order={4}>Performer</Title><IconUser size={16} /></Group></Table.Th>
										</Table.Tr>
									</Table.Thead>
									<Table.Tbody>
										{submission.instrumentalistTasks.map((task, idx) => (
											<Table.Tr key={idx}>
												<Table.Td>
													<Text>
														{task.instrument.name}
													</Text>
												</Table.Td>
												<Table.Td>
													<Group w="100%" justify="left" align="center" gap="xs">
														{task.instrumentalist && profileAvatars.get(task.instrumentalist.id)}
														<Group gap={4}>
															<Text>
																{task.instrumentalist ? task.instrumentalist.firstName : ""}
															</Text>
															<Text>
																{task.instrumentalist ? task.instrumentalist.lastName : ""}
															</Text>
														</Group>
														{task.instrumentalist?.location && (
															<Badge
																size="md"
																variant="gradient"
																gradient={{ from: task.instrumentalist.location.primaryColor || 'blue', to: task.instrumentalist.location.secondaryColor || 'blue', deg: 45 }}
															>
																{task.instrumentalist.location.displayName} Musician
															</Badge>
														)}
													</Group>
												</Table.Td>
											</Table.Tr>
										))}
									</Table.Tbody>
								</Table>
							</Paper>
						</Spoiler>
					</Stack>
				)}

				{submission.feedbackStatus === "Uploaded" && submission.feedbackProvider !== null && (
					<Stack>
						<Divider mt={20} />

						<Title order={3} c={theme.colors.blue[7]}>
							Feedback and Comments
						</Title>

						<Paper withBorder radius="md" p="md" shadow="sm" bdrs={12}>
							<Group pb="sm">
								{profileAvatars.get(submission.feedbackProvider.id) || <Avatar radius="xl" size={AVATAR_SIZE} />}
								<Stack gap={0}>
									<Text fz="sm">{feedbackProviderName}</Text>
									<Text fz="xs" c="dimmed">
										{formatRelativeWithLabel(submission.feedbackDateFulfilled, "Posted")}
									</Text>
								</Stack>
							</Group>
							{showFeedbackVideo ? (
								<Box
									style={{ position: "relative", borderRadius: 12, overflow: "hidden" }}
									onMouseEnter={() => { setHoverFeedbackVideo(true); kickInactivityTimer(); }}
									onMouseLeave={() => {
										setHoverFeedbackVideo(false)
										if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
									}}
									onMouseMove={() => { if (hoverSubmissionVideo) kickInactivityTimer(); }}
								>
									<Box style={{ position: "relative", width: "100%", aspectRatio: "16/9", background: "black" }} ref={feedbackVideoPlayerRef}>
										<ReactPlayer
											src={feedbackVideoUrl}
											width="100%"
											height="100%"
											playing={isFeedbackVideoPlaying}
											controls
											onPlay={() => {
												announcePlayback(submission.id + "-feedback");
												setIsFeedbackVideoPlaying(true);
											}}
											onPause={() => setIsFeedbackVideoPlaying(false)}
											onEnded={() => setIsFeedbackVideoPlaying(false)}
										/>
										{hoverFeedbackVideo && (
											<CenterOverlay
												isMobile={isMobile}
												playing={isFeedbackVideoPlaying}
												onClick={toggleFeedbackVideoPlay}
												faded={overlayFaded}
											/>
										)}
									</Box>
								</Box>
							) : (
								<Button variant="subtle" onClick={() => setShowFeedbackVideo((s) => !s)} style={{ marginLeft: "auto" }}>
									{showFeedbackVideo ? "Hide Feedback Video" : "Show Feedback Video"}
								</Button>
							)}
						</Paper>
					</Stack>
				)}
			</Stack>
		</Card.Section>
	);
};

/* -------------------------- publication post ------------------------ */

type PublicationPostProps = { publication: ProjectPublication };

const PublicationPost: React.FC<PublicationPostProps> = ({ publication }) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const CARD_PADDING = isMobile ? 8 : 20;
	const MEDIA_HEIGHT = isMobile ? 220 : 340;

	const [hover, setHover] = useState(false);
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [timeSec, setTimeSec] = useState(0);
	const [project, setProject] = useState<Project | null>(null);
	const [projLoading, setProjLoading] = useState(false);
	const [projError, setProjError] = useState<string | null>(null);
	const [overlayFaded, setOverlayFaded] = useState(false);
	const inactivityTimerRef = useRef<number | null>(null);
	const INACTIVITY_DELAY = 2000;

	const kickInactivityTimer = useCallback(() => {
		setOverlayFaded(false);
		if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
		inactivityTimerRef.current = window.setTimeout(() => {
			setOverlayFaded(true);
		}, INACTIVITY_DELAY) as unknown as number;
	}, []);

	useEffect(() => {
		return () => {
			if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
		};
	}, []);

	const mp3Src = useMemo(
		() => `${appSettings.S3BucketBaseUrl}ProjectPublications/${publication.id}/audio.mp3`,
		[publication.id]
	);
	const projectJsonUrl = useMemo(
		() => `${appSettings.S3BucketBaseUrl}ProjectPublications/${publication.id}/project.json`,
		[publication.id]
	);

	// load project
	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			setProjLoading(true);
			setProjError(null);
			setProject(null);
			try {
				const r = await fetch(projectJsonUrl);
				if (!r.ok) throw new Error(`(${r.status}) ${r.statusText}`);
				const txt = await r.text();
				const raw = JSON.parse(txt);
				const built = Project.fromPublicationData(publication, raw);
				if (!cancelled) {
					setProject(built);
					setProjLoading(false);
				}
			} catch (e: any) {
				if (!cancelled) {
					setProjError(e?.message ?? "Failed to load project data.");
					setProjLoading(false);
				}
			}
		};
		void run();
		return () => { cancelled = true; };
	}, [projectJsonUrl, publication]);

	// sync audio time
	useEffect(() => {
		const a = audioRef.current;
		if (!a) return;
		const onTime = () => setTimeSec(a.currentTime || 0);
		const onEnded = () => setIsPlaying(false);
		a.addEventListener("timeupdate", onTime);
		a.addEventListener("ended", onEnded);
		return () => {
			a.removeEventListener("timeupdate", onTime);
			a.removeEventListener("ended", onEnded);
		};
	}, []);

	// pause this when other media starts
	useEffect(() => {
		const unsubPlay = onHub<CustomEvent>(EVT_PLAYBACK, (e) => {
			const otherId = (e as CustomEvent).detail?.key as string | undefined;
			if (otherId && otherId !== publication.id + "-publication" && audioRef.current) {
				audioRef.current.pause();
				setIsPlaying(false);
			}
		});
		return () => { unsubPlay(); };
	}, [publication.id]);

	const togglePlay = async () => {
		const a = audioRef.current;
		if (!a) return;
		if (isPlaying) {
			a.pause();
			setIsPlaying(false);
		} else {
			announcePlayback(publication.id + "-publication");
			try { await a.play(); setIsPlaying(true); } catch {/* ignore */ }
		}
	};

	return (
		<Card.Section p={CARD_PADDING} h="100%">
			<Stack h="100%">
				<Title order={3} c={theme.colors.blue[7]}>
					{publication.publicationProjectName ?? "Untitled project"}
				</Title>

				<Box
					style={{
						position: "relative",
						borderRadius: "var(--mantine-radius-md)",
						overflow: "hidden",
						background: theme.colors.gray[0],
					}}
					h="100%"
					onMouseEnter={() => { setHover(true); kickInactivityTimer(); }}
					onMouseLeave={() => {
						setHover(false);
						setOverlayFaded(false);
						if (inactivityTimerRef.current) window.clearTimeout(inactivityTimerRef.current);
					}}
					onMouseMove={() => { if (hover) kickInactivityTimer(); }}
				>
					{projLoading && (
						<Box p="md">
							<Stack gap="sm">
								<Skeleton height={18} width="40%" />
								<Skeleton height={18} width="30%" />
								<Skeleton height={MEDIA_HEIGHT - 36} />
							</Stack>
						</Box>
					)}

					{projError && (
						<Box p="md">
							<Text c={theme.colors.red[7]} fw={600}>Could not load project data</Text>
							<Text c="dimmed" fz="sm">{projError}</Text>
						</Box>
					)}

					{project && (
						<ProjectVisual
							project={project}
							currentTimeSec={timeSec}
							beatsPerMeasure={4}
							rowHeight={Math.min(60, Math.max(24, (MEDIA_HEIGHT - 60) / Math.max(1, project.tracks.length)))}
							pxPerMeasure={10}
							showMeasureNumbers
							showPlayhead
							onScrub={(seconds) => {
								if (!audioRef.current) return;
								audioRef.current.currentTime = seconds;
								setTimeSec(seconds);
							}}
							onScrubClick={(seconds) => {
								if (!audioRef.current) return;
								audioRef.current.currentTime = seconds;
								setTimeSec(seconds);
							}}
						/>
					)}

					{project && !projLoading && !projError && hover && (
						<CenterOverlay
							isMobile={isMobile}
							playing={isPlaying}
							onClick={() => { void togglePlay(); }}
							faded={overlayFaded}
						/>
					)}
				</Box>

				<audio ref={audioRef} src={mp3Src} preload="metadata" />
			</Stack>
		</Card.Section>
	);
};

/* ------------------------- center overlay btn ------------------------ */

// CenterOverlay.tsx (or keep inline)
const CenterOverlay: React.FC<{
	isMobile: boolean | null | undefined;
	playing: boolean;
	onClick: () => void;
	faded?: boolean;
	fadeTo?: number;
}> = ({ isMobile, playing, onClick, faded = false, fadeTo = 0 }) => (
	<Box
		onClick={onClick}
		style={{
			position: "absolute",
			left: "50%",
			top: "50%",
			transform: "translate(-50%, -50%)",
			cursor: "pointer",
			background: "rgba(0,0,0,0.25)",
			borderRadius: 999,
			width: isMobile ? 64 : 84,
			height: isMobile ? 64 : 84,
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			boxShadow: "0 6px 18px rgba(0,0,0,0.35)",
			// 👇 smooth fade after inactivity
			opacity: faded ? fadeTo : 1,
			transition: "opacity 300ms ease",
		}}
	>
		{playing ? (
			<IconPlayerPause size={isMobile ? 34 : 42} color="white" />
		) : (
			<IconPlayerPlay size={isMobile ? 34 : 42} color="white" />
		)}
	</Box>
);

/* -------------------------- router component ------------------------ */

type ActivityFeedPostProps =
	| { submission: ProjectSubmission; publication?: undefined, goToUserFeed: (uid: string) => void }
	| { publication: ProjectPublication; submission?: undefined, goToUserFeed: (uid: string) => void };

const ActivityFeedPost: React.FC<ActivityFeedPostProps> = (props) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const CARD_MAX_WIDTH = isMobile ? 360 : 1080;
	const CARD_MIN_HEIGHT = props.submission ? "auto" : isMobile ? 360 : 450;

	const isSubmission = !!props.submission;
	const owner = isSubmission ? props.submission!.projectOwner : props.publication!.projectOwner;
	const headline = isSubmission ? "arranged the music in this video!" : "shared this project!";
	const rightTimeLabel = isSubmission
		? formatRelativeWithLabel(props.submission!.dateSubmitted, "Submitted")
		: formatRelativeWithLabel(props.publication!.datePublished, "Published");

	return (
		<Card
			withBorder
			radius="sm"
			p="md"
			bg="#F6F8FB"
			w="100%"
			h="100%"
			maw={CARD_MAX_WIDTH}
			mih={CARD_MIN_HEIGHT}
		>
			<Card.Section>
				<PostHeader owner={owner} headline={headline} rightTimeLabel={rightTimeLabel} goToUserFeed={props.goToUserFeed} />
			</Card.Section>
			{isSubmission ? (
				<SubmissionPost submission={props.submission!} />
			) : (
				<PublicationPost publication={props.publication!} />
			)}
		</Card>
	);
};

export default ActivityFeedPost;
